import React, { useCallback, useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import isEqual from 'lodash.isequal'
import { getSrc } from 'gatsby-plugin-image'
import styled from '@emotion/styled'
import tw from 'twin.macro'
import sanitizeHtml from 'sanitize-html'
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from 'react-accessible-accordion'
import orderBy from 'lodash.orderby'
import { Media } from 'gatsby-plugin-fresnel'
import { StoreContext } from '../../contexts/ShopifyStoreContext'
import { AddToCart } from '../../components/AddToCart'
import { formatPrice } from '../../utils/format-price'
import SEO from '../../components/seo'
import DrawerMenu from '../../components/DrawerMenu'
import Cart from '../../components/Cart'
import Header from '../../components/Header'
import Footer from '../../components/Footer/Footer'
import ProductImage from '../../components/Product/ProductImage'
import { getMetafield } from '../../utils/get-metafield'
import ProductBuyingInformation from '../../components/Product/ProductBuyingInformation'
import ProductImageCarousel from '../../components/Product/ProductImageCarousel.js'
import Chevron from '../../assets/chevron.svg'
import ProductReviews from '../../components/Product/ProductReviews'

// Styled components
const PageWrap = styled.div`
  ${tw`w-full`}

  .icon {
    ${tw`transition-transform duration-300 ease-in-out opacity-50 hover:opacity-100`}
    transform: rotate(-180deg);
  }

  .accordionIsOpen {
    .icon {
      ${tw`opacity-100`}
      transform: rotate(0deg);
    }
  }
`

const ProductInformation = styled.div`
  ${tw`w-full px-8 pt-8 sm:px-20 md:px-32 lg:px-12 pb-16 md:pb-24 xl:px-20 md:py-24 lg:py-32 lg:order-2 lg:fixed lg:right-0 lg:top-0 lg:z-30 lg:h-full bg-colekt-light-gray overflow-y-scroll text-colekt-dark-gray`}
  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
`

const TitleWrap = styled.div`
  ${tw`flex justify-between`}
`

const Title = styled.h1`
  ${tw`uppercase font-extended mb-1 leading-tight`}
  line-height: 1.2;
  letter-spacing: 1.5px;
`

const Price = styled.div`
  ${tw`font-extended uppercase tracking-wider leading-tight text-lg ml-2`}
`

const ShortDescription = styled.div`
  ${tw`font-mono mb-6 tracking-wider text-base`}
`

const Description = styled.div`
  ${tw`font-mono text-base tracking-wider mt-6 border-b mb-6`}
  p {
    ${tw`mb-5`}
  }
  ul {
    ${tw`list-disc list-outside pl-4 mb-5`}
    li {
      ${tw`mb-2`}
    }
  }
`

const ImageGallery = styled.div`
  ${tw`w-full`}
  background-color: #EDEDED
`

const AccordionTitleWrap = styled.div`
  ${tw`flex justify-between`}
`

const AccordionTitleIcon = styled.div`
  ${tw`mr-2`}
  margin-top: 3px;
`

const AccordionTitle = styled.h3`
  ${tw`block`}
  letter-spacing: 0.5px;
  font-weight: 500;
  line-height: 1.6;
  margin-left: 2px;
`

const VariantSelect = styled.select`
  ${tw`w-full mb-3 px-2 py-1 uppercase text-sm font-extended tracking-wider appearance-none border-0`}
  background: url(/chevron-down.svg) no-repeat right 12px center white;
  background-size: 10px;
  letter-spacing: 0.5px;
  font-weight: 500;
  line-height: 1.6;
  margin-left: 2px;
`

export default function Product({ data: { product, translated } }) {
  const {
    options,
    variants,
    variants: [initialVariant],
    priceRangeV2,
    title,
    description,
    descriptionHtml,
    metafields,
    handle,
    media,
    seo,
    featuredImage,
  } = product
  const { client } = useContext(StoreContext)

  const [variant, setVariant] = useState({ ...initialVariant })

  const productVariant = client.product.helpers.variantForOptions(product, variant) || variant

  const [available, setAvailable] = useState(productVariant.availableForSale)

  const checkAvailability = useCallback(
    (productId) => {
      client.product.fetch(productId).then((fetchedProduct) => {
        const result =
          fetchedProduct?.variants.filter(
            // eslint-disable-next-line no-shadow
            (variant) => variant.id === productVariant.shopifyId
          ) ?? []

        if (result.length > 0) {
          setAvailable(result[0].available)
        }
      })
    },
    [productVariant.shopifyId, client.product]
  )

  const hasVariants = variants.length > 1
  const hasImages = media.length > 0
  // const hasMultipleImages = true || images.length > 1

  const imagesOrdered = orderBy(
    media,
    // eslint-disable-next-line no-shadow
    [(image) => (image.altText ? image.altText.slice(-1) : image.altText)],
    ['asc']
  )

  const [imageGallery, setImageGallery] = useState(imagesOrdered)

  const handleOptionChange = (index, event) => {
    const { value } = event.target

    if (value === '') {
      return
    }

    const currentOptions = [...variant.selectedOptions]

    currentOptions[index] = {
      ...currentOptions[index],
      value,
    }

    // eslint-disable-next-line no-shadow
    const selectedVariant = variants.find((variant) => isEqual(currentOptions, variant.selectedOptions))

    setVariant({ ...selectedVariant })

    setImageGallery(imagesOrdered.filter((image) => image.image.altText.includes(selectedVariant.title)))
  }

  useEffect(() => {
    checkAvailability(product.storefrontId)
  }, [productVariant.storefrontId, checkAvailability, product.storefrontId])

  const price = formatPrice(priceRangeV2.minVariantPrice.currencyCode, variant.price)

  const shortDescription = getMetafield(metafields, 'subtitle')
  const ingredients = getMetafield(metafields, 'ingredients')
  const collectionTitle = getMetafield(metafields, 'collection_title')
  const collectionContent = getMetafield(metafields, 'collection_content')
  const shippingTitle = getMetafield(metafields, 'shipping_title')
  const shippingInformation = getMetafield(metafields, 'shipping_information')

  useEffect(() => {
    window.dataLayer = window.dataLayer || []

    window.dataLayer.push({
      event: 'view_item',
      ecommerce: {
        items: [
          {
            item_name: product?.title,
            item_id: variant?.sku,
            price: variant?.price,
            item_brand: 'Colekt',
            item_category: '',
            item_variant: variant?.title,
          },
        ],
      },
    })
  }, [])

  return (
    <>
      <SEO
        title={title}
        description={seo.description || description}
        image={getSrc(featuredImage.gatsbyImageData)}
        path={`product/${handle}/`}
      />
      <Header />
      <DrawerMenu />
      <Cart />
      <PageWrap className="whiteonblack aside__padding">
        <Media lessThan="lg">
          <ProductImageCarousel images={imageGallery} />
        </Media>
        <Media greaterThanOrEqual="lg">
          {hasImages && (
            <ImageGallery>
              <div role="group" aria-label="gallery" aria-describedby="instructions">
                {imageGallery?.map((image) => (
                  <ProductImage image={image.image} key={`product-image-${image.id}`} />
                ))}
              </div>
            </ImageGallery>
          )}
        </Media>
        <ProductInformation className="aside__width">
          <TitleWrap>
            <Title>{title}</Title>
            <Price>{price}</Price>
          </TitleWrap>

          <ShortDescription>{shortDescription}</ShortDescription>

          <fieldset>
            {hasVariants &&
              options.map(({ shopifyId, name, values }, index) => (
                <div key={shopifyId}>
                  <VariantSelect aria-label="Variants" onChange={(event) => handleOptionChange(index, event)}>
                    <option value="">{`Select ${name}`}</option>
                    {values.map((value) => (
                      <option value={value} key={`${name}-${value}`}>
                        {value}
                      </option>
                    ))}
                  </VariantSelect>
                </div>
              ))}
          </fieldset>

          <AddToCart
            product={product}
            variant={productVariant}
            variantId={productVariant.storefrontId}
            quantity={1}
            available={available}
          />

          <ProductBuyingInformation
            available={available}
            inventoryQuantity={productVariant.inventoryQuantity}
            shippingTitleAlt={shippingTitle}
            shippingBodyAlt={shippingInformation}
          />

          <Description dangerouslySetInnerHTML={{ __html: sanitizeHtml(descriptionHtml) }} />

          <ProductReviews sku={variant.sku} />

          <Accordion allowZeroExpanded className="lg:mb-32">
            {ingredients && (
              <AccordionItem className="border-b mb-6 pb-6" activeClassName="border-b mb-6 pb-6 accordionIsOpen">
                <AccordionItemHeading>
                  <AccordionItemButton className="text-base uppercase font-extended tracking-wider focus:outline-none">
                    <AccordionTitleWrap>
                      <AccordionTitle>
                        {translated?.globalSettings.localizedStrings.ingredients || 'Ingredients'}
                      </AccordionTitle>
                      <AccordionTitleIcon>
                        <Chevron className="icon" />
                      </AccordionTitleIcon>
                    </AccordionTitleWrap>
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel className="py-2 text-base font-mono">
                  <p>{ingredients}</p>
                </AccordionItemPanel>
              </AccordionItem>
            )}
            {collectionContent && (
              <AccordionItem className="border-b mb-6 pb-6" activeClassName="border-b mb-6 pb-6 accordionIsOpen">
                <AccordionItemHeading>
                  <AccordionItemButton className="text-base uppercase font-extended tracking-wider focus:outline-none">
                    <AccordionTitleWrap>
                      <AccordionTitle>{collectionTitle}</AccordionTitle>
                      <AccordionTitleIcon>
                        <Chevron className="icon" />
                      </AccordionTitleIcon>
                    </AccordionTitleWrap>
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel className="py-2 text-base font-mono">
                  <p>{collectionContent}</p>
                </AccordionItemPanel>
              </AccordionItem>
            )}
          </Accordion>
        </ProductInformation>
        <Footer />
      </PageWrap>
    </>
  )
}

Product.propTypes = {
  data: PropTypes.object.isRequired,
}

export const query = graphql`
  query ($id: String!) {
    product: shopifyProduct(id: { eq: $id }) {
      title
      description
      descriptionHtml
      tags
      priceRangeV2 {
        maxVariantPrice {
          amount
          currencyCode
        }
        minVariantPrice {
          amount
          currencyCode
        }
      }
      storefrontId
      handle
      featuredImage {
        localFile {
          childImageSharp {
            gatsbyImageData(
              width: 1200
              height: 630
              placeholder: NONE
              quality: 90
              transformOptions: { cropFocus: CENTER }
            )
          }
        }
      }
      media {
        ... on ShopifyMediaImage {
          id
          image {
            altText
            localFile {
              childImageSharp {
                gatsbyImageData(
                  width: 1400
                  height: 1000
                  placeholder: NONE
                  quality: 90
                  transformOptions: { cropFocus: CENTER }
                )
              }
            }
          }
        }
      }
      metafields {
        value
        key
      }
      seo {
        description
      }
      variants {
        availableForSale
        inventoryQuantity
        storefrontId
        shopifyId
        title
        price
        sku
        selectedOptions {
          name
          value
        }
      }
      options {
        name
        values
        shopifyId
      }
    }
    translated: wp {
      globalSettings {
        localizedStrings {
          ingredients
        }
      }
    }
  }
`
